/**
 * TimeSync — NTP-style clock synchronization with the server.
 *
 * Takes multiple round-trip samples, calculates offset using:
 *   offset = serverTime - (requestTime + rtt / 2)
 * Uses the median offset for robustness.
 */

import { API, TIME_SYNC_SAMPLES, TIME_SYNC_INTERVAL } from '../config';
import { apiFetch } from '../deviceId';

// Random jitter ±50% of interval to prevent thundering herd with 60k+ clients
const JITTER_FACTOR = 0.5;
function jitteredInterval(base) {
  const jitter = base * JITTER_FACTOR * (2 * Math.random() - 1);
  return Math.max(base * 0.5, base + jitter);
}

// How much slower than the fastest round trip a sample may be and still count
// as "low latency". The lowest-RTT samples carry the least path asymmetry.
const RTT_CLUSTER_FACTOR = 1.5;

/**
 * Pick the best clock offset from a set of round-trip samples (#319).
 *
 * NTP best practice: the sample with the LOWEST round-trip time has the least
 * path asymmetry, so its offset carries the smallest RTT/2 error. We keep the
 * low-RTT cluster (rtt <= minRtt * RTT_CLUSTER_FACTOR) and take its MEDIAN
 * offset — robust against a single fluke while rejecting the slow, high-bias
 * samples. The previous code medianed over ALL samples, which kept the
 * asymmetry bias of slow mobile links — exactly the clients that fattened the
 * cross-device p95 tail (EBO Do 29.10: p50 ~21ms, p95 322ms).
 *
 * @param {{offset:number, rtt:number}[]} samples
 * @returns {{offset:number, quality:number}|null} null if no valid samples
 */
export function selectOffset(samples = []) {
  const valid = (samples || []).filter(
    (s) => s && Number.isFinite(s.offset) && Number.isFinite(s.rtt)
  );
  if (valid.length === 0) return null;

  const byRtt = [...valid].sort((a, b) => a.rtt - b.rtt);
  const minRtt = byRtt[0].rtt;
  const cluster = byRtt.filter((s) => s.rtt <= minRtt * RTT_CLUSTER_FACTOR);

  const offsets = cluster.map((s) => s.offset).sort((a, b) => a - b);
  const offset = offsets[Math.floor(offsets.length / 2)];
  const quality =
    offsets.length > 1
      ? Math.round(offsets[offsets.length - 1] - offsets[0])
      : 0;
  return { offset, quality };
}

class TimeSync {
  constructor() {
    this.offset = 0;
    this.synced = false;
    this.quality = null; // sample offset spread in ms (lower = tighter sync)
    this._timeoutId = null;
  }

  /**
   * Perform a single round-trip sample
   * @returns {Promise<{offset:number, rtt:number}>} Offset estimate + its RTT
   */
  async _sample() {
    const requestTime = Date.now();
    const response = await apiFetch(API.time());
    const data = await response.json();
    const responseTime = Date.now();

    const rtt = responseTime - requestTime;
    const offset = data.serverTime - (requestTime + rtt / 2);
    return { offset, rtt };
  }

  /**
   * Run multiple samples and pick the best offset (see selectOffset).
   * @param {number} count - Number of samples
   * @returns {Promise<number>} Chosen offset
   */
  async sync(count = TIME_SYNC_SAMPLES) {
    const samples = [];
    for (let i = 0; i < count; i++) {
      try {
        // Small random gap between samples (50-250ms) to spread load
        if (i > 0) await new Promise((r) => setTimeout(r, 50 + Math.random() * 200));
        samples.push(await this._sample());
      } catch {
        // Skip failed samples
      }
    }

    const picked = selectOffset(samples);
    if (!picked) {
      console.warn('[TimeSync] All samples failed, using offset=0');
      return 0;
    }

    this.offset = picked.offset;
    this.synced = true;
    // Spread of the KEPT (low-RTT) cluster = confidence of the chosen offset
    // (lower is better). Drives the aggregate's avgSyncQuality.
    this.quality = picked.quality;
    return this.offset;
  }

  /** Sample offset spread in ms from the last sync (lower = tighter). */
  getQuality() {
    return this.quality;
  }

  /**
   * Get current server time estimate
   * @returns {number} Estimated server timestamp in ms
   */
  getServerTime() {
    return Date.now() + this.offset;
  }

  /**
   * Start periodic re-sync with jittered intervals.
   * Each cycle uses a randomised delay (±50% of TIME_SYNC_INTERVAL)
   * so that 60k+ clients don't all hit the server simultaneously.
   */
  startPeriodicSync() {
    this.stopPeriodicSync();
    const tick = () => {
      this._timeoutId = setTimeout(() => {
        this.sync().catch(() => {});
        tick();
      }, jitteredInterval(TIME_SYNC_INTERVAL));
    };
    tick();
  }

  /**
   * Stop periodic re-sync
   */
  stopPeriodicSync() {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
  }
}

// Singleton
export const timeSync = new TimeSync();
